import type { Edge, Node } from '@xyflow/react'

/**
 * Workflows on the board (Huanrui, 2026-10-16)
 * =============================================================
 * A run of cards on the board -- say Image Generation, then Colour grade,
 * then Upscale -- kept as one card that does the same to other pictures.
 *
 * BUILD. Select the cards (Shift + triple-click picks the whole chain) and
 * right-click > Build workflow. The pictures that feed the chain and that
 * the chain did not make are its INPUTS; the cards are its STEPS; what the
 * last cards made is its OUTPUT; a picture one card made and the next one
 * took is passed along inside. All of that is worked out here (analyse) and
 * shown to be changed before anything moves (WorkflowBuilder.tsx): each
 * input named, put in order, kept to change ("replace") or baked in
 * ("fixed"); a card left out; another picture made an output too.
 *
 * MERGE. The steps and what they made go onto a page of their own that the
 * page tabs do not show; the input pictures stay where they were, wired into
 * the new card's named ports, and the output pictures stay, wired out of it.
 * Double-click the card (or Open) to see that page, and change anything on
 * it as on any board; Back works the workflow out again from what is there.
 *
 * RUN. Pictures wired into each port; the first of every port goes together,
 * then the second, and so on (a port with one picture gives it to every run).
 * Each run copies the steps on to the workflow's page and runs them in order,
 * each card the way its own Run does; the results come out to the right of
 * the card. Paid steps wait their turn: no more than the person's limit (5)
 * at once, counted with everything else they run.
 *
 * SHARED with a project: the card and its page are part of the board. Added
 * to a person's toolbar it is copied into their account (workflowStore.ts),
 * and from then on changing it on a board does not change the toolbar's.
 *
 * Steps that depend on where something is in a picture (Edit area's box)
 * carry it as a share of the picture, so it falls in the same place on the
 * next one. `locate` is where an AI that finds the place itself will plug in.
 */

/** Cards a workflow can hold, and how each is run. */
export const STEP_TYPES = new Set(['picture', 'upscale', 'video', 'angle', 'vector', 'grade', 'edit', 'skill'])
/** Cards it cannot hold yet, and why. */
export const REFUSED: Record<string, string> = {
  text: 'Edit text reads the words in each picture, so it cannot be repeated on another yet',
  moodboard: 'Mood board takes a frame; workflows take pictures for now',
  library: 'A Library card searches rather than changes a picture',
  background: 'Background is not in workflows yet',
  eraser: 'Eraser paints on one picture, so it cannot be repeated on another',
}
/** Steps that spend credits: these wait for a free place under the limit. */
export const PAID_TYPES = new Set(['picture', 'upscale', 'video', 'angle', 'edit', 'skill'])

export interface WorkflowInput {
  /** The input picture's node on the workflow's page (its default picture). */
  id: string
  name: string
  /** Changed every run, or baked into the workflow. */
  mode: 'replace' | 'fixed'
}

export interface WorkflowDef {
  name: string
  glyph: string
  /** In the order the card shows its ports. */
  inputs: WorkflowInput[]
  /** Cards whose pictures come out of the workflow. */
  outputs: string[]
  /** How many cards. */
  steps: number
  /** Credits one run took when it was built, if known; 0 = free. */
  estimate: number | null
  builtAt: string
  /** The toolbar entry it was added to or came from, if any. */
  toolbarId?: string
  /**
   * Reserved for steps that need a place in each picture (Edit area's box):
   * 'share' -- the same share of the picture, as now; 'ai' -- found by an AI
   * in each picture, later.
   */
  locate?: 'share' | 'ai'
}

export interface Analysis {
  steps: Node[]
  /** Pictures that feed the steps and that no step made. */
  inputs: Node[]
  /** Pictures one step made and another took. */
  passed: Node[]
  /** Pictures the last steps made. */
  results: Node[]
  /** Steps whose pictures are not taken by another step. */
  terminal: string[]
  refused: { node: Node; why: string }[]
  /** Credits the steps took when last run, if every paid one says. */
  estimate: number | null
}

const isStep = (node: Node | undefined) => Boolean(node && STEP_TYPES.has(node.type ?? ''))

/** The chain in `chosen` (selected nodes) worked out from the wires. */
export function analyse(chosen: Node[], all: Node[], edges: Edge[]): Analysis {
  const byId = new Map(all.map((node) => [node.id, node]))
  const refused = chosen.filter((node) => REFUSED[node.type ?? '']).map((node) => ({ node, why: REFUSED[node.type ?? '']! }))
  const steps = chosen.filter(isStep)
  const stepIds = new Set(steps.map((node) => node.id))
  const made = new Set(edges.filter((edge) => stepIds.has(edge.source)).map((edge) => edge.target))
  const feeding = new Set(edges.filter((edge) => stepIds.has(edge.target)).map((edge) => edge.source))
  const pictures = (ids: Set<string>) =>
    [...ids].map((id) => byId.get(id)).filter((node): node is Node => Boolean(node) && node!.type === 'image')
  const inputs = pictures(new Set([...feeding].filter((id) => !made.has(id)))).sort(byPlace)
  const passed = pictures(new Set([...made].filter((id) => feeding.has(id))))
  const results = pictures(new Set([...made].filter((id) => !feeding.has(id))))
  const terminal = steps
    .filter((step) => !edges.some((edge) => edge.source === step.id && feeding.has(edge.target)))
    .map((step) => step.id)
  let estimate: number | null = 0
  for (const step of steps) {
    if (!PAID_TYPES.has(step.type ?? '')) continue
    const credits = Number((step.data as { credits?: unknown }).credits)
    if (Number.isFinite(credits) && credits > 0) estimate = (estimate ?? 0) + credits
    else estimate = null
    if (estimate === null) break
  }
  return { steps, inputs, passed, results, terminal, refused, estimate }
}

/** Top to bottom, then left to right. */
export function byPlace(a: Node, b: Node): number {
  return Math.abs(a.position.y - b.position.y) > 40 ? a.position.y - b.position.y : a.position.x - b.position.x
}

/** The steps in an order where each comes after every step that feeds it. */
export function inOrder(steps: Node[], edges: Edge[]): Node[] {
  const ids = new Set(steps.map((node) => node.id))
  // card -> picture -> card: who feeds whom.
  const madeBy = new Map<string, string>()
  for (const edge of edges) if (ids.has(edge.source)) madeBy.set(edge.target, edge.source)
  const before = new Map<string, Set<string>>(steps.map((node) => [node.id, new Set<string>()]))
  for (const edge of edges) {
    if (!ids.has(edge.target)) continue
    const from = madeBy.get(edge.source)
    if (from && from !== edge.target) before.get(edge.target)!.add(from)
  }
  const out: Node[] = []
  const done = new Set<string>()
  const left = [...steps].sort(byPlace)
  while (left.length > 0) {
    const at = left.findIndex((node) => [...before.get(node.id)!].every((id) => done.has(id)))
    const next = left.splice(at < 0 ? 0 : at, 1)[0]!
    done.add(next.id)
    out.push(next)
  }
  return out
}

/** The ports of a workflow card for its inputs that change. */
export const portOf = (inputId: string) => `in-${inputId}`

export const WORKFLOW_GLYPH = '⧉'

/**
 * What runs left on a workflow's page (Huanrui, 2026-10-16: the workflow's
 * layer shows the workflow and nothing else): the steps and input pictures a
 * run copies (data.wfRun), whatever those made, and so on down the line.
 * Never the workflow itself -- nothing of it is wired from a run's copies.
 */
export function runLeftovers(nodes: Node[], edges: Edge[], page: string): Set<string> {
  const here = new Set(nodes.filter((node) => (node as { page?: string }).page === page).map((node) => node.id))
  const out = new Set(
    nodes
      .filter((node) => here.has(node.id) && (node.data as { wfRun?: unknown }).wfRun)
      .map((node) => node.id),
  )
  for (let grew = out.size > 0; grew; ) {
    grew = false
    for (const edge of edges) {
      if (out.has(edge.source) && here.has(edge.target) && !out.has(edge.target)) {
        out.add(edge.target)
        grew = true
      }
    }
  }
  return out
}

/**
 * Steps whose pictures come out because one of the workflow's own pictures
 * they made is marked (right-click, Mark; 2026-10-16): kept, even in the
 * middle of the chain.
 */
export function markedSteps(template: Node[], edges: Edge[]): Set<string> {
  const byId = new Map(template.map((node) => [node.id, node]))
  const out = new Set<string>()
  for (const edge of edges) {
    const from = byId.get(edge.source)
    const to = byId.get(edge.target)
    if (from && to && isStep(from) && to.type === 'image' && (to.data as { mark?: unknown }).mark) out.add(from.id)
  }
  return out
}
